import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Sparkles, Loader2, Check, X, Search, BookMarked } from 'lucide-react';
import { toast } from '../utils/toast';

const API = `${window.__API_BASE}/api`;

const FIELD_LABELS = {
  brand_tone: 'Brand Tone',
  cast_profile: 'Cast Profile',
  wardrobe_style: 'Wardrobe',
  world_design: 'World / Set Design',
};

const COSTUME_FIELD_LABELS = {
  costume_palette_primary: 'Hero Colors (dominant)',
  costume_palette_accents: 'Accent / Neutral Colors',
  costume_palette_avoid: 'Avoid Colors',
  costume_formality_range: 'Formality Range',
  costume_fabric_hints: 'Fabrics / Finish',
  costume_north_star: 'Costume North Star',
};

const BRAND_CATEGORY_OPTIONS = [
  { value: 'bank_finance', label: 'Bank / Finance / Insurance' },
  { value: 'tech_gadget', label: 'Tech / Gadget / Consumer Electronics' },
  { value: 'fashion_lifestyle', label: 'Fashion / Lifestyle' },
  { value: 'fmcg', label: 'FMCG / Everyday Consumer' },
  { value: 'luxury_auto_premium', label: 'Luxury / Auto / Premium' },
  { value: 'sports_athleisure', label: 'Sports / Athleisure' },
  { value: 'beauty_personal_care', label: 'Beauty / Personal Care' },
  { value: 'generic', label: 'Generic / Other' },
];

const SILHOUETTE_OPTIONS = [
  { value: 'subtle', label: 'Subtle — conservative, tailored, clean' },
  { value: 'moderate', label: 'Moderate — smart-street, wearable' },
  { value: 'expressive', label: 'Expressive — editorial, statement' },
];

export const BrandDNAPanel = ({ projectId, project, onAttached }) => {
  const [profiles, setProfiles] = useState([]);
  const [attachedId, setAttachedId] = useState(project?.brand_profile_id || null);
  const [brandName, setBrandName] = useState('');
  const [product, setProduct] = useState('');
  const [researching, setResearching] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [edits, setEdits] = useState(null);

  const loadProfiles = () => {
    axios.get(`${API}/brand-dna`).then(({ data }) => setProfiles(data.profiles || [])).catch(() => {});
  };
  useEffect(loadProfiles, []);
  useEffect(() => { setAttachedId(project?.brand_profile_id || null); }, [project?.brand_profile_id]);

  // Auto-populate brand/product from the project script (user can edit).
  useEffect(() => {
    if (!projectId || project?.brand_profile_id) return;
    axios.get(`${API}/brand-dna/suggest/${projectId}`).then(({ data }) => {
      setBrandName(prev => prev || data.brand_name || '');
      setProduct(prev => prev || data.product || '');
    }).catch(() => {});
  }, [projectId, project?.brand_profile_id]); // eslint-disable-line

  const attach = async (pid) => {
    try {
      await axios.post(`${API}/projects/${projectId}/brand-profile`, { brand_profile_id: pid });
      setAttachedId(pid);
      setEdits(null);
      onAttached?.(pid);
      toast.success(pid ? 'Brand DNA attached — Asset Bible prompts will be brand-tailored.' : 'Brand DNA detached.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Attach failed');
    }
  };

  const research = async (nameOverride, productOverride, updateId) => {
    const bn = (nameOverride ?? brandName).trim();
    if (!bn) return toast.error('Enter a brand name');
    setResearching(true);
    setDraft(null);
    try {
      const { data } = await axios.post(`${API}/brand-dna/research`, {
        brand_name: bn, product: (productOverride ?? product).trim(), project_id: projectId,
      }, { timeout: 240000 });
      setDraft(updateId ? { ...data, _updateId: updateId } : data);
      toast.success('Brand researched — review & edit the DNA below, then save.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Research failed');
    } finally {
      setResearching(false);
    }
  };

  const saveAndAttach = async () => {
    setSaving(true);
    try {
      const { _updateId, ...payload } = draft;
      if (_updateId) {
        await axios.put(`${API}/brand-dna/${_updateId}`, payload);
        toast.success('Brand DNA updated with fresh research.');
      } else {
        const { data } = await axios.post(`${API}/brand-dna`, payload);
        await attach(data.id);
      }
      setDraft(null);
      setBrandName('');
      setProduct('');
      loadProfiles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveEdits = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/brand-dna/${attachedId}`, edits);
      setEdits(null);
      loadProfiles();
      toast.success('Brand DNA changes saved.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const attached = profiles.find(p => p.id === attachedId);

  // ── Costume Directive fields renderer (used in both draft & attached views)
  const renderCostumeDirective = ({ source, onChange, testidPrefix }) => (
    <div className="mt-2 pt-2 border-t border-purple-500/15">
      <div className="text-[10px] text-purple-300/80 uppercase tracking-wider mb-1.5 font-semibold">
        Costume Directive · brand-locked wardrobe rules
      </div>
      <div className="space-y-1.5">
        <div>
          <label className="text-[10px] text-purple-300/60 uppercase tracking-wider">Brand Category</label>
          <select
            value={source.brand_category || 'generic'}
            onChange={(e) => onChange('brand_category', e.target.value)}
            className="w-full px-2 py-1.5 rounded-lg text-[11px] bg-white/[0.03] border border-white/[0.08] focus:border-purple-500/40 text-white/70 outline-none"
            data-testid={`${testidPrefix}-brand_category`}
          >
            {BRAND_CATEGORY_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] text-purple-300/60 uppercase tracking-wider">Silhouette Band</label>
          <select
            value={source.costume_silhouette_band || 'moderate'}
            onChange={(e) => onChange('costume_silhouette_band', e.target.value)}
            className="w-full px-2 py-1.5 rounded-lg text-[11px] bg-white/[0.03] border border-white/[0.08] focus:border-purple-500/40 text-white/70 outline-none"
            data-testid={`${testidPrefix}-costume_silhouette_band`}
          >
            {SILHOUETTE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        {Object.keys(COSTUME_FIELD_LABELS).map(k => (
          <div key={k}>
            <label className="text-[10px] text-purple-300/60 uppercase tracking-wider">{COSTUME_FIELD_LABELS[k]}</label>
            <textarea
              value={source[k] || ''}
              onChange={(e) => onChange(k, e.target.value)}
              rows={k === 'costume_north_star' ? 2 : 1}
              className="w-full px-2 py-1.5 rounded-lg text-[11px] leading-snug bg-white/[0.03] border border-white/[0.08] focus:border-purple-500/40 text-white/70 resize-none outline-none"
              data-testid={`${testidPrefix}-${k}`}
              placeholder={
                k === 'costume_palette_primary' ? 'e.g. saffron orange, deep purple' :
                k === 'costume_palette_accents' ? 'e.g. off-white, charcoal' :
                k === 'costume_palette_avoid' ? 'e.g. neon green, hot pink' :
                k === 'costume_formality_range' ? 'e.g. smart_casual_to_formal' :
                k === 'costume_fabric_hints' ? 'e.g. matte tailored wool, cotton poplin' :
                'e.g. tailored, confident, understated — banker energy with a modern twist'
              }
            />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="mb-4 p-3 rounded-xl" style={{ background: 'rgba(168,85,247,0.05)', border: '1px solid rgba(168,85,247,0.15)' }} data-testid="brand-dna-panel">
      <div className="flex items-center gap-2 mb-2">
        <BookMarked className="w-3.5 h-3.5 text-purple-400" />
        <span className="text-xs font-semibold text-purple-300 uppercase tracking-wider">Brand DNA</span>
        <span className="text-[10px] text-white/30">web-researched brand tailoring for the Asset Bible · optional</span>
      </div>

      {attached ? (
        <div className="px-3 py-2 rounded-lg bg-purple-500/10 border border-purple-500/20" data-testid="brand-dna-attached">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-purple-200 font-medium">
              {attached.brand_name}{attached.product ? <span className="text-white/40 font-normal"> — {attached.product}</span> : null}
            </span>
            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
              <button onClick={() => research(attached.brand_name, attached.product || '', attached.id)} disabled={researching}
                className="px-2 py-1 rounded-md text-[10px] font-medium bg-purple-500/20 text-purple-300 border border-purple-500/30 hover:bg-purple-500/30 flex items-center gap-1 disabled:opacity-40"
                data-testid="brand-dna-research-again-btn">
                {researching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                {researching ? 'Researching…' : 'Research Again'}
              </button>
              <button onClick={() => attach(null)} className="text-[10px] text-white/40 hover:text-red-400" data-testid="brand-dna-detach-btn">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <div className="space-y-1.5" data-testid="brand-dna-attached-fields">
            {Object.keys(FIELD_LABELS).map(k => {
              const saved = Array.isArray(attached[k]) ? attached[k].join(', ') : (attached[k] || '');
              return (
                <div key={k}>
                  <span className="text-[10px] text-purple-300/60 uppercase tracking-wider">{FIELD_LABELS[k]}</span>
                  <textarea
                    value={edits?.[k] ?? saved}
                    onChange={e => setEdits(prev => ({ ...(prev || {}), [k]: e.target.value }))}
                    rows={3}
                    className="w-full px-2 py-1.5 rounded-lg text-[11px] leading-snug bg-white/[0.03] border border-white/[0.08] focus:border-purple-500/40 text-white/70 resize-none outline-none"
                    data-testid={`brand-dna-attached-${k}`}
                  />
                </div>
              );
            })}
            {renderCostumeDirective({
              source: { ...attached, ...(edits || {}) },
              onChange: (k, v) => setEdits(prev => ({ ...(prev || {}), [k]: v })),
              testidPrefix: 'brand-dna-attached',
            })}
          </div>
          {edits && (
            <div className="flex items-center gap-2 pt-1.5">
              <button onClick={saveEdits} disabled={saving}
                className="px-3 py-1 rounded-lg text-[11px] font-semibold bg-purple-500/30 text-purple-200 border border-purple-500/40 hover:bg-purple-500/40 flex items-center gap-1.5 disabled:opacity-40"
                data-testid="brand-dna-save-edits-btn">
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                Save Changes
              </button>
              <button onClick={() => setEdits(null)} className="text-[10px] text-white/40 hover:text-white/70" data-testid="brand-dna-cancel-edits-btn">Cancel</button>
            </div>
          )}
        </div>
      ) : (
        <>
          {profiles.length > 0 && (
            <div className="flex items-center gap-2 mb-2">
              <select
                className="flex-1 px-2 py-1.5 rounded-lg text-xs bg-white/[0.04] border border-white/[0.1] text-white/70"
                defaultValue=""
                onChange={(e) => e.target.value && attach(e.target.value)}
                data-testid="brand-dna-library-select"
              >
                <option value="" disabled>Attach from brand library…</option>
                {profiles.map(p => <option key={p.id} value={p.id}>{p.brand_name}{p.product ? ` — ${p.product}` : ''}</option>)}
              </select>
            </div>
          )}
          <div className="flex items-center gap-2">
            <input value={brandName} onChange={e => setBrandName(e.target.value)} placeholder="Brand name (e.g. AU Bank)"
              className="flex-1 px-2 py-1.5 rounded-lg text-xs bg-white/[0.04] border border-white/[0.1] text-white/80 placeholder-white/25"
              data-testid="brand-dna-name-input" />
            <input value={product} onChange={e => setProduct(e.target.value)} placeholder="Product (optional)"
              className="flex-1 px-2 py-1.5 rounded-lg text-xs bg-white/[0.04] border border-white/[0.1] text-white/80 placeholder-white/25"
              data-testid="brand-dna-product-input" />
            <button onClick={() => research()} disabled={researching}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-purple-500/20 text-purple-300 border border-purple-500/30 hover:bg-purple-500/30 transition-colors flex items-center gap-1.5 disabled:opacity-40"
              data-testid="brand-dna-research-btn">
              {researching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
              {researching ? 'Researching…' : 'Research Brand'}
            </button>
          </div>
        </>
      )}

      {draft && (
        <div className="mt-3 space-y-2" data-testid="brand-dna-draft">
          {Object.keys(FIELD_LABELS).map(k => (
            <div key={k}>
              <label className="text-[10px] text-purple-300/60 uppercase tracking-wider">{FIELD_LABELS[k]}</label>
              <textarea
                value={Array.isArray(draft[k]) ? draft[k].join(', ') : (draft[k] || '')}
                onChange={e => setDraft(d => ({ ...d, [k]: e.target.value }))}
                rows={3}
                className="w-full px-2 py-1.5 rounded-lg text-xs bg-white/[0.04] border border-white/[0.1] text-white/75 resize-none"
                data-testid={`brand-dna-field-${k}`}
              />
            </div>
          ))}
          {renderCostumeDirective({
            source: draft,
            onChange: (k, v) => setDraft(d => ({ ...d, [k]: v })),
            testidPrefix: 'brand-dna-draft',
          })}
          <div className="flex items-center gap-2 pt-1">
            <button onClick={saveAndAttach} disabled={saving}
              className="px-4 py-1.5 rounded-lg text-xs font-semibold bg-purple-500/30 text-purple-200 border border-purple-500/40 hover:bg-purple-500/40 flex items-center gap-1.5 disabled:opacity-40"
              data-testid="brand-dna-save-attach-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              {draft._updateId ? 'Update Brand DNA' : 'Save to Library & Attach'}
            </button>
            <button onClick={() => setDraft(null)} className="text-[10px] text-white/40 hover:text-white/70" data-testid="brand-dna-discard-btn">Discard</button>
          </div>
        </div>
      )}
    </div>
  );
};
